/*
 * ArcGIS JS 4.17 draws symbols onto hidden 2D canvases and reads the pixels straight back
 * (getImageData / toDataURL): legend swatches, hatch pattern tiles. Chrome keeps a canvas on the
 * GPU by default, so every read-back stalls until the GPU catches up - roughly 20-30 ms a call,
 * about five calls per hatched legend swatch. That was most of the multi-second freeze on map
 * load, and every hatched layer added to it.
 *
 * `willReadFrequently: true` tells the browser to keep a canvas in ordinary memory instead, so
 * the read-backs become plain memory copies (benchmarked ~200x faster; the map's hatch textures
 * come out pixel-identical). Esri set the same flag in their own rasterizer from SDK 4.28 on. We
 * are pinned to 4.24, so this adds it from outside: getContext is wrapped, and the flag is added
 * only when the caller is ArcGIS code (served from js.arcgis.com). Every other canvas in the app
 * keeps the browser default.
 */

const ARCGIS_SCRIPT_HOST = 'js.arcgis.com';
const PATCHED_FLAG = '__arcgisCanvasReadbackFix';

type GetContext = HTMLCanvasElement['getContext'];
type ContextSettings = { willReadFrequently?: boolean; [setting: string]: unknown };

/*
 * True when the function that called getContext is ArcGIS code. The stack is captured inside the
 * wrapped getContext, so frame 0 is the wrapper and frame 1 is its caller. Only the caller is
 * checked, not the whole stack, so an app canvas created during an ArcGIS callback (e.g. a React
 * render triggered by a map click) keeps the default.
 */
export function isArcgisCaller(stack: string): boolean {
    // V8 frames read "    at fn (url:1:2)", Firefox/Safari frames "fn@url:1:2". V8's leading
    // "Error" line matches neither and drops out.
    const frames = stack.split('\n').filter((line) => line.includes(' at ') || line.includes('@'));
    return (frames[1] || '').includes(ARCGIS_SCRIPT_HOST);
}

/*
 * Must run before the SDK creates its first canvas. Installs once per page; returns an uninstall
 * function (used by tests). `callerCheck` is injectable for tests.
 */
export function installArcgisCanvasReadbackFix(callerCheck: (stack: string) => boolean = isArcgisCaller): () => void {
    const proto = HTMLCanvasElement.prototype as HTMLCanvasElement & { [PATCHED_FLAG]?: boolean };
    if (proto[PATCHED_FLAG]) {
        return () => undefined;
    }

    const originalGetContext = proto.getContext;
    // A canvas's settings are fixed by its first getContext call, and the SDK calls getContext on
    // the same canvas over and over, so only the first call needs the stack check.
    const decided = new WeakSet<HTMLCanvasElement>();

    function getContext(this: HTMLCanvasElement, contextId: string, settings?: ContextSettings) {
        let effective = settings;
        if (contextId === '2d' && !decided.has(this)) {
            decided.add(this);
            if ((!settings || settings.willReadFrequently === undefined) && callerCheck(new Error().stack || '')) {
                effective = { ...settings, willReadFrequently: true };
            }
        }
        return originalGetContext.call(this, contextId, effective);
    }

    proto.getContext = getContext as unknown as GetContext;
    proto[PATCHED_FLAG] = true;

    return () => {
        proto.getContext = originalGetContext;
        delete proto[PATCHED_FLAG];
    };
}



// WEBPACK FOOTER //
// ./src/components/project/sections/GISComponents/ArcgisCanvasReadback.ts